import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Activity, Eye, Users, Radio, ChevronRight, RefreshCw, TrendingUp } from "lucide-react";
import { Button } from "../components/ui/button";

function humanTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
}

export default function Admin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/analytics");
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000); // auto-refresh every 15s
    return () => clearInterval(id);
  }, [load]);

  if (error) {
    return (
      <div className="grid-bg min-h-[calc(100vh-4rem)]">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="neo-flat p-8 text-center text-red-300" data-testid="admin-error">
            {error}
          </div>
        </div>
      </div>
    );
  }

  const t = data?.totals || {};

  return (
    <div className="grid-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tighter text-slate-50 flex items-center gap-3">
              <Activity className="w-10 h-10 text-cyan-300" /> Analytics
            </h1>
            <p className="text-slate-400 mt-1">Live traffic, unique viewers, and active sessions.</p>
          </div>
          <Button
            onClick={load}
            disabled={loading}
            data-testid="admin-refresh-button"
            className="border border-cyan-400/60 bg-cyan-400 hover:bg-cyan-300 text-slate-950"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* Headline stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <BigStat
            testId="stat-pageviews-total"
            label="Page Views"
            value={t.pageviews_total}
            sub={`${t.pageviews_today ?? 0} today · ${t.pageviews_7d ?? 0} last 7d`}
            icon={<Eye className="w-5 h-5 text-cyan-300" />}
            glow="glow-cyan"
          />
          <BigStat
            testId="stat-unique-viewers"
            label="Unique Viewers"
            value={t.unique_viewers_total}
            sub={`${t.unique_viewers_today ?? 0} today`}
            icon={<Users className="w-5 h-5 text-violet-300" />}
            glow="glow-violet"
          />
          <BigStat
            testId="stat-active-sessions"
            label="Active (5 min)"
            value={t.active_sessions}
            sub="Live browser sessions"
            icon={<TrendingUp className="w-5 h-5 text-amber-300" />}
            glow="glow-amber"
          />
          <BigStat
            testId="stat-live-ws"
            label="Live WS"
            value={t.live_ws_online}
            sub={`${t.in_queue ?? 0} in matchmaking queue`}
            icon={<Radio className="w-5 h-5 text-emerald-300" />}
            glow="glow-lime"
          />
        </div>

        {/* Top paths + Recent events */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="neo-flat p-6">
            <h2 className="font-display font-bold text-xl mb-4 text-slate-100">Top Pages</h2>
            {!data?.top_paths?.length ? (
              <div className="text-slate-500 text-sm py-8 text-center" data-testid="top-paths-empty">
                No page views yet.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="top-paths-list">
                {data.top_paths.map((p) => (
                  <li key={p.path} className="flex items-center justify-between p-2 border border-slate-700 rounded-lg bg-slate-900/60">
                    <code className="font-mono-stat text-sm text-cyan-200 truncate">{p.path}</code>
                    <div className="flex items-center gap-3 text-xs font-mono-stat">
                      <span className="text-slate-400">{p.uniques} uniq</span>
                      <span className="text-amber-300 font-bold">{p.views} views</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="neo-flat p-6">
            <h2 className="font-display font-bold text-xl mb-4 text-slate-100">Recent Events</h2>
            {!data?.recent_events?.length ? (
              <div className="text-slate-500 text-sm py-8 text-center" data-testid="recent-events-empty">
                No events yet.
              </div>
            ) : (
              <ul className="space-y-1 max-h-[420px] overflow-y-auto pr-1" data-testid="recent-events-list">
                {data.recent_events.map((e) => (
                  <li key={e.id} className="flex items-center gap-2 py-2 border-b border-slate-800/70 last:border-b-0 text-sm">
                    <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <code className="font-mono-stat text-cyan-200 truncate flex-1">{e.path}</code>
                    <span className="text-xs text-slate-500 truncate max-w-[110px]">
                      {e.username || (e.is_guest ? "guest" : "anon")}
                    </span>
                    <span className="text-xs font-mono-stat text-slate-500 shrink-0">{humanTime(e.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function BigStat({ label, value, sub, icon, testId, glow }) {
  return (
    <div className={`neo-flat p-5 ${glow || ""}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="font-mono-stat text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
        {icon}
      </div>
      <div className="font-display font-black text-4xl mt-2 text-slate-50" data-testid={`${testId}-value`}>
        {value ?? 0}
      </div>
      {sub && <div className="text-xs font-mono-stat text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
